/* eslint-disable unicorn/filename-case -- TODO rename later */
import { LinkIcon } from "lucide-react";
import { FunctionComponent, SVGProps } from "react";

import { underscoreToSpaced } from "~/lib/utils";

interface AppLabel {
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  tmName: string;
  name: string;
}

// Keyed by AuthIntegrationType (the auth-provider value carried on an integration), since that is
// what every consumer looks the label up by. Trademark notes kept per each provider's branding terms.
const appLabels: Record<string, AppLabel> = {
  // https://developers.google.com/workspace/drive/api/guides/branding
  google_accounts: {
    icon: LinkIcon,
    tmName: "Google Drive™",
    name: "Google Drive",
  },
  // Microsoft requires approval to use the logo. https://cdn-dynmedia-1.microsoft.com/is/content/microsoftcorp/microsoft/mscle/documents/presentations/Microsoft_365_trademark_guidelines_2024_.pdf
  microsoft_entra: {
    icon: LinkIcon,
    tmName: "Microsoft 365®",
    name: "Microsoft 365",
  },
  // Dassault requires approval learn how to use the logo (not a typo). https://branding.3ds.com/registration-details/4761fceb-5c4b-48bb-b963-f3d541138b8d
  dassault_3d_passport: {
    icon: LinkIcon,
    tmName: "Dassault Systèmes 3DEXPERIENCE®",
    name: "Dassault Systèmes 3DEXPERIENCE",
  },
  teamwork_cloud: {
    icon: LinkIcon,
    tmName: "Dassault Systèmes Teamwork Cloud®",
    name: "Dassault Systèmes Teamwork Cloud",
  },
  // PTC requires approval to use the logo. https://www.ptc.com/en/documents/policies/trademark
  windchill: {
    icon: LinkIcon,
    tmName: "PTC Windchill®",
    name: "PTC Windchill",
  },
  ping_id: {
    icon: LinkIcon,
    tmName: "Ping Identity®",
    name: "Ping Identity",
  },
  // Siemens requires approval to use the logo. https://www.sw.siemens.com/
  siemens_teamcenter: {
    icon: LinkIcon,
    tmName: "Siemens Teamcenter®",
    name: "Siemens Teamcenter",
  },
  // IBM requires approval to use the logo. https://www.ibm.com/legal/copytrade
  ibm_doors: {
    icon: LinkIcon,
    tmName: "IBM Rational DOORS®",
    name: "IBM Rational DOORS",
  },
  // DOORS Next (IBM ELM) is a distinct connect target from DOORS Classic (its own metadata extension,
  // read over OSLC), though the doors-module reports the same tool_key.
  ibm_doors_next: {
    icon: LinkIcon,
    tmName: "IBM Rational DOORS® Next",
    name: "IBM Rational DOORS Next",
  },
  // 3DEXPERIENCE CATIA and Enovia are distinct first-class providers (each with its own metadata
  // extension the 3dexperience-module / 3dexperience-enovia-module accept); both authenticate via
  // 3D Passport but are cataloged and connected separately.
  dassault_3dexperience_catia: {
    icon: LinkIcon,
    tmName: "Dassault Systèmes 3DEXPERIENCE® CATIA",
    name: "3DEXPERIENCE CATIA",
  },
  dassault_3dexperience_enovia: {
    icon: LinkIcon,
    tmName: "Dassault Systèmes 3DEXPERIENCE® Enovia",
    name: "3DEXPERIENCE Enovia",
  },
  // Atlassian Jira and Confluence are distinct first-class providers (each with its own metadata
  // extension the jira-module accepts); both authenticate to Atlassian Cloud with an email + API
  // token but are cataloged and connected separately.
  atlassian_jira: {
    icon: LinkIcon,
    tmName: "Atlassian Jira®",
    name: "Atlassian Jira",
  },
  atlassian_confluence: {
    icon: LinkIcon,
    tmName: "Atlassian Confluence®",
    name: "Atlassian Confluence",
  },
};

// The connected-file link library and the modules identify a file's source provider by an "app host"
// vocabulary that differs from AuthIntegrationType (e.g. a Google integration links `google_drive`
// files). Map an integration's auth type to that app host so the link form, its URL validation, and
// the generated metadata file all agree. Providers with no distinct app host map to themselves.
const appHostByAuthType: Record<string, string> = {
  google_accounts: "google_drive",
  microsoft_entra: "microsoft_365",
  dassault_3d_passport: "dassault_3d_experience",
  teamwork_cloud: "teamwork_cloud",
  windchill: "windchill",
  // Teamcenter and DOORS use the same value for the auth provider and the connected-file host,
  // so the generated metadata is exactly istari_siemens_teamcenter_metadata / istari_ibm_doors_metadata.
  siemens_teamcenter: "siemens_teamcenter",
  ibm_doors: "ibm_doors",
  // DOORS Next maps to its own host so the generated metadata is istari_ibm_doors_next_metadata.
  ibm_doors_next: "ibm_doors_next",
  // CATIA and Enovia use the same value for the auth provider and the connected-file host, so the
  // generated metadata is exactly istari_dassault_3dexperience_catia_metadata / ..._enovia_metadata.
  dassault_3dexperience_catia: "dassault_3dexperience_catia",
  dassault_3dexperience_enovia: "dassault_3dexperience_enovia",
  // Jira and Confluence use the same value for the auth provider and the connected-file host, so the
  // generated metadata is exactly istari_atlassian_jira_metadata / istari_atlassian_confluence_metadata.
  atlassian_jira: "atlassian_jira",
  atlassian_confluence: "atlassian_confluence",
};

const authTypeByAppHost: Record<string, string> = Object.fromEntries(
  Object.entries(appHostByAuthType).map(([authType, host]) => [host, authType]),
);

export function getConnectedFileAppHost(authIntegrationType: string): string {
  return appHostByAuthType[authIntegrationType] ?? authIntegrationType;
}

// Resolve a provider's display label. `appLabels` is keyed by AuthIntegrationType, but connected-file
// callers look up by an app-host alias (e.g. `google_drive`), so fold those back to the auth type
// first. Degrade to a humanized form of the raw value for anything uncurated so callers never
// dereference an undefined label.
export function getAppLabel(type: string): AppLabel {
  const key = appLabels[type] ? type : (authTypeByAppHost[type] ?? type);
  return appLabels[key] ?? { icon: LinkIcon, tmName: underscoreToSpaced(type), name: underscoreToSpaced(type) };
}
